import {RentFreePeriod, RentalRow} from '@/services/auditService/interface/wp-row';
import {
    addDays,
    addYears,
    computeRentProrated,
    formatMonYY,
    monthsBetweenInclusive,
    parseYMD,
    toNumber,
} from '@/utils/dataUtils';

/** Format Date as DD/MM/YYYY. */
export function formatRentalDDMMYYYY(d: Date): string {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = String(d.getFullYear());
    return `${dd}/${mm}/${yyyy}`;
}

/** True when toDate falls within [periodFrom, periodTo] inclusive. */
export function isToDateInCurrentPeriod(
    toDate: string | null | undefined,
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
): boolean {
    const to = parseYMD(toDate);
    const pf = parseYMD(periodFrom);
    const pt = parseYMD(periodTo);
    if (!to || !pf || !pt) return false;
    return to.getTime() >= pf.getTime() && to.getTime() <= pt.getTime();
}

export function hasActiveRenewal(row: Pick<RentalRow, 'renewal'> | null | undefined): boolean {
    const r = row?.renewal;
    if (!r) return false;
    return Boolean(String(r.fromDate || '').trim() || String(r.toDate || '').trim() || String(r.monthlyRent || '').trim());
}

/** Next-day fromDate for renewal segment (= prior toDate + 1). */
export function computeRenewalFromDate(toDate: string | null | undefined): string {
    const to = parseYMD(toDate);
    if (!to) return '';
    return formatRentalDDMMYYYY(addDays(to, 1));
}

function normalizeAddressKey(address: string | null | undefined): string {
    return String(address || '').trim().toLowerCase();
}

function sameCalendarDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

/** Inclusive intersection of two DD/MM/YYYY ranges; null if empty / incomplete. */
export function intersectRentFreePeriod(
    period: RentFreePeriod,
    windowFrom: string | null | undefined,
    windowTo: string | null | undefined,
): RentFreePeriod | null {
    const pf = parseYMD(period?.from);
    const pt = parseYMD(period?.to);
    const wf = parseYMD(windowFrom);
    const wt = parseYMD(windowTo);
    if (!pf || !pt || !wf || !wt || pf > pt || wf > wt) return null;
    const start = pf > wf ? pf : wf;
    const end = pt < wt ? pt : wt;
    if (start > end) return null;
    return {from: formatRentalDDMMYYYY(start), to: formatRentalDDMMYYYY(end)};
}

/**
 * Split UI rent-free intervals across primary / renewal leases for persist.
 * Spanning intervals (e.g. across toDate → fromDate+1) become two clipped records.
 */
export function partitionRentFreePeriodsByLeases(
    periods: RentFreePeriod[] | null | undefined,
    lease1From: string | null | undefined,
    lease1To: string | null | undefined,
    lease2From: string | null | undefined,
    lease2To: string | null | undefined,
): {primary: RentFreePeriod[]; renewal: RentFreePeriod[]} {
    const primary: RentFreePeriod[] = [];
    const renewal: RentFreePeriod[] = [];
    for (const period of normalizeRentFreePeriods(periods)) {
        const inPrimary = intersectRentFreePeriod(period, lease1From, lease1To);
        if (inPrimary) primary.push(inPrimary);
        const inRenewal = intersectRentFreePeriod(period, lease2From, lease2To);
        if (inRenewal) renewal.push(inRenewal);
    }
    return {primary, renewal};
}

/** Coalesce adjacent rent-free intervals (to + 1 day == next.from). */
export function coalesceAdjacentRentFreePeriods(
    periods: RentFreePeriod[] | null | undefined,
): RentFreePeriod[] {
    const parsed = normalizeRentFreePeriods(periods)
        .map((p) => ({from: parseYMD(p.from), to: parseYMD(p.to), raw: p}))
        .filter((p): p is {from: Date; to: Date; raw: RentFreePeriod} => !!p.from && !!p.to && p.from <= p.to)
        .sort((a, b) => a.from.getTime() - b.from.getTime());

    if (parsed.length === 0) return [];

    const out: RentFreePeriod[] = [];
    let curFrom = parsed[0].from;
    let curTo = parsed[0].to;
    for (let i = 1; i < parsed.length; i++) {
        const next = parsed[i];
        const expectedNext = addDays(curTo, 1);
        if (sameCalendarDay(next.from, expectedNext) || next.from <= curTo) {
            if (next.to > curTo) curTo = next.to;
            continue;
        }
        out.push({from: formatRentalDDMMYYYY(curFrom), to: formatRentalDDMMYYYY(curTo)});
        curFrom = next.from;
        curTo = next.to;
    }
    out.push({from: formatRentalDDMMYYYY(curFrom), to: formatRentalDDMMYYYY(curTo)});
    return out;
}

/** Merge primary + renewal rent-free lists for single UI row (coalesce across boundary). */
export function combineRentFreePeriodsForDisplay(
    primaryPeriods: RentFreePeriod[] | null | undefined,
    renewalPeriods: RentFreePeriod[] | null | undefined,
): RentFreePeriod[] {
    return coalesceAdjacentRentFreePeriods([
        ...normalizeRentFreePeriods(primaryPeriods),
        ...normalizeRentFreePeriods(renewalPeriods),
    ]);
}

/**
 * True when a complete rent-free interval has any day outside the allowed lease union.
 * Incomplete intervals are ignored.
 */
export function doRentFreePeriodsExceedLeaseBounds(
    periods: RentFreePeriod[] | null | undefined,
    leaseFrom: string | null | undefined,
    leaseTo: string | null | undefined,
): boolean {
    const lf = parseYMD(leaseFrom);
    const lt = parseYMD(leaseTo);
    if (!lf || !lt) return false;
    for (const period of normalizeRentFreePeriods(periods)) {
        const pf = parseYMD(period.from);
        const pt = parseYMD(period.to);
        if (!pf || !pt || pf > pt) continue;
        if (pf < lf || pt > lt) return true;
    }
    return false;
}

function buildMergedRenewalRow(primary: RentalRow, renewal: RentalRow): RentalRow {
    // Only rewrite rentFree when the renewal row actually carries its own intervals.
    // Otherwise keep primary.rentFreePeriods untouched (same reference via spread).
    const renewalHasRentFree = normalizeRentFreePeriods(renewal.rentFreePeriods).length > 0;
    return {
        ...primary,
        ...(renewalHasRentFree
            ? {
                rentFreePeriods: combineRentFreePeriodsForDisplay(
                    primary.rentFreePeriods,
                    renewal.rentFreePeriods,
                ),
            }
            : {}),
        renewal: {
            fromDate: String(renewal.fromDate || ''),
            toDate: String(renewal.toDate || ''),
            monthlyRent: String(renewal.monthlyRent || ''),
            id: renewal.id,
            clientKey: renewal.clientKey,
        },
    };
}

/**
 * Merge consecutive same-address leases where lease2.fromDate === lease1.toDate + 1.
 * Used as FE fallback when GET returns unmerged legacy rows.
 * Preserves original row order; returns the same array reference when nothing to merge.
 */
export function mergeConnectedRentalRows(rows: RentalRow[] | null | undefined): RentalRow[] {
    const list = rows || [];
    if (list.length <= 1) return list;

    const used = new Set<number>();
    const result: RentalRow[] = [];
    let didMerge = false;

    for (let i = 0; i < list.length; i++) {
        if (used.has(i)) continue;
        const current = list[i];
        if (hasActiveRenewal(current)) {
            result.push(current);
            used.add(i);
            continue;
        }

        const addr = normalizeAddressKey(current.address);
        const currentTo = parseYMD(current.toDate);
        const currentFrom = parseYMD(current.fromDate);

        // current is primary → find renewal after it
        let renewalIdx = -1;
        if (addr && currentTo) {
            const expectedFrom = addDays(currentTo, 1);
            for (let j = 0; j < list.length; j++) {
                if (j === i || used.has(j) || hasActiveRenewal(list[j])) continue;
                if (normalizeAddressKey(list[j].address) !== addr) continue;
                const otherFrom = parseYMD(list[j].fromDate);
                if (otherFrom && sameCalendarDay(otherFrom, expectedFrom)) {
                    renewalIdx = j;
                    break;
                }
            }
        }

        // current is renewal → find primary before it (legacy row order may put renewal first)
        if (renewalIdx < 0 && addr && currentFrom) {
            const expectedPrevTo = addDays(currentFrom, -1);
            for (let j = 0; j < list.length; j++) {
                if (j === i || used.has(j) || hasActiveRenewal(list[j])) continue;
                if (normalizeAddressKey(list[j].address) !== addr) continue;
                const otherTo = parseYMD(list[j].toDate);
                if (otherTo && sameCalendarDay(otherTo, expectedPrevTo)) {
                    const primary = list[j];
                    const renewalRow = current;
                    used.add(i);
                    used.add(j);
                    didMerge = true;
                    result.push(buildMergedRenewalRow(primary, renewalRow));
                    renewalIdx = -2;
                    break;
                }
            }
        }

        if (renewalIdx === -2) continue;

        if (renewalIdx >= 0) {
            const renewalRow = list[renewalIdx];
            used.add(i);
            used.add(renewalIdx);
            didMerge = true;
            result.push(buildMergedRenewalRow(current, renewalRow));
        } else {
            used.add(i);
            result.push(current);
        }
    }

    return didMerge ? result : list;
}

/**
 * Split merged renewal rows into two persistable rows before save.
 * Rent-free intervals are partitioned onto each lease (spanning ones are clipped/split).
 * No-op (same reference) when no row has an active renewal.
 */
export function splitRenewalRowsForSave(rows: RentalRow[] | null | undefined): RentalRow[] {
    const list = rows || [];
    if (!list.some((row) => hasActiveRenewal(row))) {
        return list;
    }
    const out: RentalRow[] = [];
    for (const row of list) {
        // Non-renewal rows: keep original object (no rent-free rewrite).
        if (!hasActiveRenewal(row) || !row.renewal) {
            out.push(row);
            continue;
        }

        const renewal = row.renewal;
        const renewalFrom = computeRenewalFromDate(row.toDate) || String(renewal.fromDate || '');
        const {primary: primaryRentFree, renewal: renewalRentFree} = partitionRentFreePeriodsByLeases(
            row.rentFreePeriods,
            row.fromDate,
            row.toDate,
            renewalFrom,
            renewal.toDate,
        );

        out.push({
            ...row,
            rentFreePeriods: primaryRentFree,
            renewal: undefined,
        });
        out.push({
            id: renewal.id,
            annualId: row.annualId,
            lineId: row.lineId,
            code: row.code || 'PL-AE-REN',
            clientKey: String(renewal.clientKey || ''),
            address: String(row.address || ''),
            agreementDate: String(row.agreementDate || ''),
            landlord: String(row.landlord || ''),
            tenant: String(row.tenant || ''),
            leaseAgreementLink: row.leaseAgreementLink ?? null,
            tenantPersonId: row.tenantPersonId ?? null,
            tenantPersonIdNo: row.tenantPersonIdNo ?? null,
            rentalPeriod: '',
            fromDate: renewalFrom,
            toDate: String(renewal.toDate || ''),
            monthlyRent: String(renewal.monthlyRent || ''),
            rentFreePeriods: renewalRentFree,
            cancellationDate: '',
            rentalDepositChange: null,
            otherInfo: {},
            headerId: String(row.headerId || ''),
            adjustments: null,
            renewal: undefined,
        });
    }
    return out;
}

export function computeMergedOneYearRentFromWindow(
    nextYearWindow: {start: Date; end: Date} | null | undefined,
    row: Pick<RentalRow, 'fromDate' | 'toDate' | 'monthlyRent' | 'renewal'> | null | undefined,
): number {
    if (!row) return 0;
    let sum = computeOneYearRentFromWindow(nextYearWindow, row.fromDate, row.toDate, row.monthlyRent);
    if (hasActiveRenewal(row) && row.renewal) {
        sum += computeOneYearRentFromWindow(
            nextYearWindow,
            row.renewal.fromDate,
            row.renewal.toDate,
            row.renewal.monthlyRent,
        );
    }
    return sum;
}

export function computeMergedOverYearRentFromWindow(
    nextYearWindow: {start: Date; end: Date} | null | undefined,
    row: Pick<RentalRow, 'fromDate' | 'toDate' | 'monthlyRent' | 'renewal'> | null | undefined,
): number {
    if (!row) return 0;
    let sum = computeOverYearRentFromWindow(nextYearWindow, row.fromDate, row.toDate, row.monthlyRent);
    if (hasActiveRenewal(row) && row.renewal) {
        sum += computeOverYearRentFromWindow(
            nextYearWindow,
            row.renewal.fromDate,
            row.renewal.toDate,
            row.renewal.monthlyRent,
        );
    }
    return sum;
}

/** Non-split: (m1 + m2) * 2; split: first-row override only. */
export function resolveMergedRentalDeposit(
    row: Pick<RentalRow, 'monthlyRent' | 'rentalDepositChange' | 'otherInfo' | 'renewal'> | null | undefined,
): number {
    if (isRentalDepositSplitActive(row)) {
        return Number(row?.rentalDepositChange) || 0;
    }
    const base = toNumber(row?.monthlyRent);
    const renewalRent = hasActiveRenewal(row) ? toNumber(row?.renewal?.monthlyRent) : 0;
    return (base + renewalRent) * 2;
}

/** Month amount across primary + renewal segments (rent free applied per overlapping segment). */
export function computeMergedRentalMonthAmount(
    month: string,
    row: Pick<RentalRow, 'fromDate' | 'toDate' | 'monthlyRent' | 'rentFreePeriods' | 'renewal' | 'cancellationDate'> | null | undefined,
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
): number {
    if (!row) return 0;
    const cancellationDate = String(row.cancellationDate || '').trim()
        ? row.cancellationDate
        : undefined;
    // Single lease: identical to computeRentalMonthAmount (no renewal branch).
    if (!hasActiveRenewal(row) || !row.renewal) {
        return computeRentalMonthAmount(month, {
            periodFrom,
            periodTo,
            fromDate: row.fromDate,
            toDate: row.toDate,
            monthlyRent: row.monthlyRent,
            rentFreePeriods: normalizeRentFreePeriods(row.rentFreePeriods),
            ...(cancellationDate ? {cancellationDate} : {}),
        });
    }
    const freePeriods = normalizeRentFreePeriods(row.rentFreePeriods);
    const primary = computeRentalMonthAmount(month, {
        periodFrom,
        periodTo,
        fromDate: row.fromDate,
        toDate: row.toDate,
        monthlyRent: row.monthlyRent,
        rentFreePeriods: freePeriods,
        ...(cancellationDate ? {cancellationDate} : {}),
    });
    const renewal = computeRentalMonthAmount(month, {
        periodFrom,
        periodTo,
        fromDate: row.renewal.fromDate,
        toDate: row.renewal.toDate,
        monthlyRent: row.renewal.monthlyRent,
        rentFreePeriods: freePeriods,
        ...(cancellationDate ? {cancellationDate} : {}),
    });
    return primary + renewal;
}

/** Overall lease bounds for drawer month list (primary ∪ renewal). */
export function getMergedLeaseDateBounds(
    row: Pick<RentalRow, 'fromDate' | 'toDate' | 'renewal' | 'cancellationDate'> | null | undefined,
    options?: {applyCancellation?: boolean},
): {fromDate: string; toDate: string} | null {
    if (!row) return null;
    const from1 = parseYMD(row.fromDate);
    const to1 = parseYMD(row.toDate);
    if (!from1 || !to1) return null;
    let from = from1;
    let to = to1;
    if (hasActiveRenewal(row) && row.renewal) {
        const from2 = parseYMD(row.renewal.fromDate);
        const to2 = parseYMD(row.renewal.toDate);
        if (from2 && from2 < from) from = from2;
        if (to2 && to2 > to) to = to2;
    }
    const applyCancellation = options?.applyCancellation !== false
        && Boolean(String(row.cancellationDate || '').trim());
    if (applyCancellation) {
        const effectiveTo = resolveEffectiveLeaseToDate(formatRentalDDMMYYYY(to), row.cancellationDate);
        const toResolved = parseYMD(effectiveTo) || to;
        return {fromDate: formatRentalDDMMYYYY(from), toDate: formatRentalDDMMYYYY(toResolved)};
    }
    return {fromDate: formatRentalDDMMYYYY(from), toDate: formatRentalDDMMYYYY(to)};
}

export function computeMergedRentalPeriodTotal(
    row: Pick<RentalRow, 'fromDate' | 'toDate' | 'monthlyRent' | 'rentFreePeriods' | 'renewal' | 'cancellationDate'> | null | undefined,
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
    deletedMonths?: string[],
    monthChanges?: Record<string, number>,
): number {
    if (!row) return 0;
    const bounds = getMergedLeaseDateBounds(row);
    if (!bounds) return 0;
    const pf = parseYMD(periodFrom);
    const pt = parseYMD(periodTo);
    const rf = parseYMD(bounds.fromDate);
    const rt = parseYMD(bounds.toDate);
    if (!pf || !pt || !rf || !rt) return 0;

    const effectiveStart = rf > pf ? rf : pf;
    const effectiveEnd = rt < pt ? rt : pt;
    if (effectiveStart > effectiveEnd) return 0;

    const deleted = deletedMonths || [];
    const changes = monthChanges || {};
    const visibleMonths = monthsBetweenInclusive(effectiveStart, effectiveEnd)
        .map(formatMonYY)
        .filter((month) => !deleted.includes(month));

    return visibleMonths.reduce((sum, month) => {
        const calculated = computeMergedRentalMonthAmount(month, row, periodFrom, periodTo);
        const adjustment = Number(changes[month]) || 0;
        return sum + calculated + adjustment;
    }, 0);
}

export type RentalPeriodInput = {
    periodFrom: string | null | undefined;
    periodTo: string | null | undefined;
    fromDate: string | null | undefined;
    toDate: string | null | undefined;
    monthlyRent: number | string | null | undefined;
    rentFreePeriods?: RentFreePeriod[] | null | undefined;
    /** When set, rent charges stop on this day (inclusive). */
    cancellationDate?: string | null | undefined;
    deletedMonths?: string[];
    monthChanges?: Record<string, number>;
};

const daysBetweenInclusive = (from: Date, to: Date) => {
    const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
};

/** Add calendar months, clamping day to last day of target month (e.g. Jan 31 + 1M → Feb 28/29). */
const addCalendarMonths = (date: Date, monthsToAdd: number): Date => {
    const base = new Date(date.getFullYear(), date.getMonth() + monthsToAdd, 1);
    const lastDay = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    return new Date(base.getFullYear(), base.getMonth(), Math.min(date.getDate(), lastDay));
};

/**
 * Normalize rent-free periods to a clean array (even when only one interval).
 * Empty / incomplete intervals are dropped.
 */
export function normalizeRentFreePeriods(
    periods?: RentFreePeriod[] | null | undefined,
): RentFreePeriod[] {
    if (!Array.isArray(periods)) return [];
    return periods
        .map((p) => ({
            from: String(p?.from || '').trim(),
            to: String(p?.to || '').trim(),
        }))
        .filter((p) => p.from || p.to);
}

/**
 * Returns true when any two complete rent-free intervals overlap (inclusive dates).
 * Incomplete intervals (missing from/to or from > to) are ignored for overlap checks.
 */
export function doRentFreePeriodsOverlap(periods: RentFreePeriod[] | null | undefined): boolean {
    const parsed = (periods || [])
        .map((p) => ({from: parseYMD(p.from), to: parseYMD(p.to)}))
        .filter((p): p is {from: Date; to: Date} => !!p.from && !!p.to && p.from <= p.to)
        .sort((a, b) => a.from.getTime() - b.from.getTime());

    for (let i = 1; i < parsed.length; i++) {
        if (parsed[i].from <= parsed[i - 1].to) {
            return true;
        }
    }
    return false;
}

/**
 * Convert a date range to dynamic "X M" / "Y D" / "X M Y D".
 *
 * Full calendar months are counted by anniversary (start → next month same day − 1 day).
 * - 01/04 ~ 30/04 → "1 M"
 * - 02/04 ~ 30/04 → "29 D"
 * - 01/02/2024 ~ 29/02/2024 (leap) → "1 M"
 * - 01/02/2024 ~ 15/03/2024 → "1 M 15 D"
 */
export function formatRentFreeDuration(
    fromDate: string | null | undefined,
    toDate: string | null | undefined,
): string {
    const from = parseYMD(fromDate);
    const to = parseYMD(toDate);
    if (!from || !to || from > to) return '-';

    let months = 0;
    while (true) {
        const nextAnniversary = addCalendarMonths(from, months + 1);
        const monthEnd = new Date(
            nextAnniversary.getFullYear(),
            nextAnniversary.getMonth(),
            nextAnniversary.getDate() - 1,
        );
        if (monthEnd <= to) {
            months += 1;
        } else {
            break;
        }
    }

    const remStart = addCalendarMonths(from, months);
    const days = remStart <= to ? daysBetweenInclusive(remStart, to) : 0;

    if (months <= 0 && days <= 0) return '-';
    if (months > 0 && days > 0) return `${months} M ${days} D`;
    if (months > 0) return `${months} M`;
    return `${days} D`;
}

/** Format rent-free periods for table display. */
export function formatRentFreePeriodsDuration(
    periods?: RentFreePeriod[] | null | undefined,
): string {
    const normalized = normalizeRentFreePeriods(periods);
    if (normalized.length === 0) return '-';
    const parts = normalized
        .map((p) => formatRentFreeDuration(p.from, p.to))
        .filter((s) => s !== '-');
    return parts.length > 0 ? parts.join(', ') : '-';
}

/**
 * Effective lease end for rent calc: cancellation day is still chargeable (inclusive).
 * No cancellation → original toDate (unchanged behavior).
 */
export function resolveEffectiveLeaseToDate(
    toDate: string | null | undefined,
    cancellationDate: string | null | undefined,
): string | null | undefined {
    const to = parseYMD(toDate);
    if (!to) return toDate;
    const cancel = parseYMD(cancellationDate);
    if (!cancel) return toDate;
    if (cancel.getTime() < to.getTime()) {
        return formatRentalDDMMYYYY(cancel);
    }
    return toDate;
}

const maxDate = (a: Date, b: Date) => (a > b ? a : b);
const minDate = (a: Date, b: Date) => (a < b ? a : b);

/** Per-month prorated rent (matches RentalDrawer breakdown). */
export function computeRentalMonthAmount(
    month: string,
    input: Omit<RentalPeriodInput, 'deletedMonths' | 'monthChanges'>,
): number {
    const pf = parseYMD(input.periodFrom);
    const pt = parseYMD(input.periodTo);
    const rf = parseYMD(input.fromDate);
    // Only rewrite end date when cancellation is set — otherwise identical to original toDate path.
    const rt = parseYMD(
        String(input.cancellationDate || '').trim()
            ? resolveEffectiveLeaseToDate(input.toDate, input.cancellationDate)
            : input.toDate,
    );
    if (!pf || !pt || !rf || !rt) return 0;

    const monthlyRent = toNumber(input.monthlyRent);
    const effectiveStart = maxDate(rf, pf);
    const effectiveEnd = minDate(rt, pt);
    if (effectiveStart > effectiveEnd) return 0;

    const allMonths = monthsBetweenInclusive(effectiveStart, effectiveEnd);
    const monthDate = allMonths.find((d) => formatMonYY(d) === month);
    if (!monthDate) return 0;

    const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const chargeStart = maxDate(monthStart, effectiveStart);
    const chargeEnd = minDate(monthEnd, effectiveEnd);
    if (chargeStart > chargeEnd) return 0;

    let chargeableDays = daysBetweenInclusive(chargeStart, chargeEnd);

    const freePeriods = normalizeRentFreePeriods(input.rentFreePeriods);
    for (const period of freePeriods) {
        const freeFrom = parseYMD(period.from);
        const freeTo = parseYMD(period.to);
        if (!freeFrom || !freeTo) continue;
        const freeStart = maxDate(chargeStart, freeFrom);
        const freeEnd = minDate(chargeEnd, freeTo);
        if (freeStart <= freeEnd) {
            chargeableDays -= daysBetweenInclusive(freeStart, freeEnd);
        }
    }

    if (chargeableDays <= 0) return 0;

    const monthDays = monthEnd.getDate();
    return monthlyRent / monthDays * chargeableDays;
}

/** Sum prorated monthly rents within the annual period (respects drawer edits). */
export function computeRentalPeriodTotal(input: RentalPeriodInput): number {
    const pf = parseYMD(input.periodFrom);
    const pt = parseYMD(input.periodTo);
    const rf = parseYMD(input.fromDate);
    const rt = parseYMD(
        String(input.cancellationDate || '').trim()
            ? resolveEffectiveLeaseToDate(input.toDate, input.cancellationDate)
            : input.toDate,
    );
    if (!pf || !pt || !rf || !rt) return 0;

    const effectiveStart = maxDate(rf, pf);
    const effectiveEnd = minDate(rt, pt);
    if (effectiveStart > effectiveEnd) return 0;

    const deletedMonths = input.deletedMonths || [];
    const monthChanges = input.monthChanges || {};
    const visibleMonths = monthsBetweenInclusive(effectiveStart, effectiveEnd)
        .map(formatMonYY)
        .filter((month) => !deletedMonths.includes(month));

    return visibleMonths.reduce((sum, month) => {
        const calculatedValue = computeRentalMonthAmount(month, input);
        const adjustment = Number(monthChanges[month]) || 0;
        return sum + calculatedValue + adjustment;
    }, 0);
}

/** Sum Other Expenses finals: expense + adjustment per row. */
export function sumExtraExpenseFinals(
    rows?: Array<{expense?: number; adjustment?: number} | null> | null,
): number {
    return (rows || []).reduce((sum, row) => {
        return sum + (Number(row?.expense) || 0) + (Number(row?.adjustment) || 0);
    }, 0);
}

/** Sum of additive month adjustments only (pending WP→WP from rental table). */
export function sumMonthAdjustments(monthChanges?: Record<string, number> | null): number {
    if (!monthChanges) return 0;
    return Object.values(monthChanges).reduce((sum, v) => sum + (Number(v) || 0), 0);
}

/** Sum of Other Expenses adjustment column only (pending WP→WP). */
export function sumExtraExpenseAdjustments(
    rows?: Array<{adjustment?: number} | null> | null,
): number {
    return (rows || []).reduce((sum, row) => sum + (Number(row?.adjustment) || 0), 0);
}

/** Sum signed amounts from a locked WP→WP adjustment list. */
export function sumAdjustmentDetails(
    list?: Array<{adjustment?: number} | null> | null,
): number {
    if (!Array.isArray(list) || list.length === 0) return 0;
    return list.reduce((sum, a) => sum + (Number(a?.adjustment) || 0), 0);
}

/** nextYearWindow = [periodTo + 1 day, periodTo + 12 months] (inclusive end). */
export function buildNextYearWindow(periodTo: string | null | undefined): {start: Date; end: Date} | null {
    const baseTo = parseYMD(periodTo);
    if (!baseTo) return null;
    const start = new Date(baseTo.getFullYear(), baseTo.getMonth(), baseTo.getDate() + 1);
    const end = new Date(start.getFullYear(), start.getMonth() + 12, start.getDate() - 1);
    return {start, end};
}

/** One year: prorated rent for lease overlap with nextYearWindow. */
export function computeOneYearRent(
    periodTo: string | null | undefined,
    fromDate: string | null | undefined,
    toDate: string | null | undefined,
    monthlyRent: number | string | null | undefined,
): number {
    return computeOneYearRentFromWindow(buildNextYearWindow(periodTo), fromDate, toDate, monthlyRent);
}

export function computeOneYearRentFromWindow(
    nextYearWindow: {start: Date; end: Date} | null | undefined,
    fromDate: string | null | undefined,
    toDate: string | null | undefined,
    monthlyRent: number | string | null | undefined,
): number {
    if (!nextYearWindow) return 0;
    const rf = parseYMD(fromDate);
    const rt = parseYMD(toDate);
    if (!rt) return 0;

    const leaseStart = rf && rf > nextYearWindow.start ? rf : nextYearWindow.start;
    const overlapEnd = rt < nextYearWindow.end ? rt : nextYearWindow.end;
    if (leaseStart > overlapEnd) return 0;

    return computeRentProrated(leaseStart, overlapEnd, toNumber(monthlyRent));
}

/** Over a year: prorated rent after nextYearWindow ends. */
export function computeOverYearRent(
    periodTo: string | null | undefined,
    fromDate: string | null | undefined,
    toDate: string | null | undefined,
    monthlyRent: number | string | null | undefined,
): number {
    return computeOverYearRentFromWindow(buildNextYearWindow(periodTo), fromDate, toDate, monthlyRent);
}

export function computeOverYearRentFromWindow(
    nextYearWindow: {start: Date; end: Date} | null | undefined,
    fromDate: string | null | undefined,
    toDate: string | null | undefined,
    monthlyRent: number | string | null | undefined,
): number {
    if (!nextYearWindow) return 0;
    const rt = parseYMD(toDate);
    if (!rt) return 0;

    const overStart = new Date(
        nextYearWindow.end.getFullYear(),
        nextYearWindow.end.getMonth(),
        nextYearWindow.end.getDate() + 1,
    );
    if (rt < overStart) return 0;

    return computeRentProrated(overStart, rt, toNumber(monthlyRent));
}

/**
 * All Rental Details "Total Months | Total" for one lease row in a Period Tab window
 * (same formula as RentalDrawer rentalTotal / main-table Total Rental base).
 */
export function computeRentalRowAllDetailsTotal(
    row: Pick<RentalRow, 'fromDate' | 'toDate' | 'monthlyRent' | 'rentFreePeriods' | 'renewal' | 'cancellationDate' | 'clientKey' | 'otherInfo'> | null | undefined,
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
    opts?: {
        deletedMonths?: string[];
        monthChanges?: Record<string, number>;
    },
): number {
    if (!row) return 0;
    const rowKey = String(row.clientKey || '');
    const otherInfo = (row.otherInfo || {}) as {
        deletedMonthsByCol?: Record<string, string[]>;
        monthChangesByCol?: Record<string, Record<string, number>>;
    };
    const deletedMonths = opts?.deletedMonths
        ?? (rowKey ? (otherInfo.deletedMonthsByCol?.[rowKey] || []) : []);
    const monthChanges = opts?.monthChanges
        ?? (rowKey ? (otherInfo.monthChangesByCol?.[rowKey] || {}) : {});

    if (hasActiveRenewal(row)) {
        return computeMergedRentalPeriodTotal(
            row,
            periodFrom,
            periodTo,
            deletedMonths,
            monthChanges,
        );
    }
    return computeRentalPeriodTotal({
        periodFrom,
        periodTo,
        fromDate: row.fromDate,
        toDate: row.toDate,
        monthlyRent: row.monthlyRent,
        rentFreePeriods: normalizeRentFreePeriods(row.rentFreePeriods),
        cancellationDate: row.cancellationDate,
        deletedMonths,
        monthChanges,
    });
}

/** True when lease from–to fully covers the Period Tab window (inclusive). */
export function rentalLeaseFullyCoversPeriod(
    row: Pick<RentalRow, 'fromDate' | 'toDate' | 'renewal' | 'cancellationDate'> | null | undefined,
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
): boolean {
    if (!row) return false;
    const bounds = getMergedLeaseDateBounds(row, {applyCancellation: true});
    const leaseFrom = parseYMD(bounds?.fromDate || row.fromDate);
    const leaseTo = parseYMD(bounds?.toDate || row.toDate);
    const pf = parseYMD(periodFrom);
    const pt = parseYMD(periodTo);
    if (!leaseFrom || !leaseTo || !pf || !pt) return false;
    return leaseFrom.getTime() <= pf.getTime() && leaseTo.getTime() >= pt.getTime();
}

/** True when lease from–to overlaps the Period Tab window at all. */
export function rentalLeaseOverlapsPeriodWindow(
    row: Pick<RentalRow, 'fromDate' | 'toDate' | 'renewal' | 'cancellationDate'> | null | undefined,
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
): boolean {
    if (!row) return false;
    const bounds = getMergedLeaseDateBounds(row, {applyCancellation: true});
    const leaseFrom = parseYMD(bounds?.fromDate || row.fromDate);
    const leaseTo = parseYMD(bounds?.toDate || row.toDate);
    const pf = parseYMD(periodFrom);
    const pt = parseYMD(periodTo);
    if (!leaseFrom || !leaseTo || !pf || !pt) return false;
    const start = leaseFrom > pf ? leaseFrom : pf;
    const end = leaseTo < pt ? leaseTo : pt;
    return start <= end;
}

/**
 * Monthly rent applicable on a calendar month (1st of month), primary vs renewal segment.
 */
function monthlyRentOnMonth(
    row: Pick<RentalRow, 'fromDate' | 'toDate' | 'monthlyRent' | 'renewal' | 'cancellationDate'>,
    monthStart: Date,
): number {
    const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
    const inSeg = (fromDate?: string | null, toDate?: string | null): boolean => {
        const rf = parseYMD(fromDate);
        let effectiveTo = toDate;
        if (String(row.cancellationDate || '').trim()) {
            effectiveTo = resolveEffectiveLeaseToDate(toDate, row.cancellationDate) ?? toDate;
        }
        const rt = parseYMD(effectiveTo);
        if (!rf || !rt) return false;
        return rf <= monthEnd && rt >= monthStart;
    };
    if (hasActiveRenewal(row) && row.renewal && inSeg(row.renewal.fromDate, row.renewal.toDate)) {
        return toNumber(row.renewal.monthlyRent);
    }
    if (inSeg(row.fromDate, row.toDate)) {
        return toNumber(row.monthlyRent);
    }
    return 0;
}

/** Raw lease∩period rent via computeRentProrated (full months + last-month day fraction). */
function computeRawLeaseRentInPeriod(
    row: Pick<RentalRow, 'fromDate' | 'toDate' | 'monthlyRent' | 'renewal' | 'cancellationDate'>,
    periodFrom: Date,
    periodTo: Date,
): number {
    const segment = (
        fromDate: string | null | undefined,
        toDate: string | null | undefined,
        monthlyRent: number | string | null | undefined,
    ): number => {
        const rf = parseYMD(fromDate);
        let effectiveTo = toDate;
        if (String(row.cancellationDate || '').trim()) {
            effectiveTo = resolveEffectiveLeaseToDate(toDate, row.cancellationDate) ?? toDate;
        }
        const rt = parseYMD(effectiveTo);
        if (!rf || !rt) return 0;
        const start = rf > periodFrom ? rf : periodFrom;
        const end = rt < periodTo ? rt : periodTo;
        if (start > end) return 0;
        return computeRentProrated(start, end, toNumber(monthlyRent));
    };

    let sum = segment(row.fromDate, row.toDate, row.monthlyRent);
    if (hasActiveRenewal(row) && row.renewal) {
        sum += segment(row.renewal.fromDate, row.renewal.toDate, row.renewal.monthlyRent);
    }
    return sum;
}

/**
 * MULTI last Tab — Over a year (Edit Rental from–to vs this Tab period):
 * 1.1 all period months covered → Monthly Rent × month count (typically ×12)
 * 1.2 partial months → full months × rent + (last-month days / days-in-month × rent)
 * 1.3 no overlap → 0
 */
export function computeMultiLastTabOverAYear(
    row: Pick<RentalRow, 'fromDate' | 'toDate' | 'monthlyRent' | 'renewal' | 'cancellationDate'> | null | undefined,
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
): number {
    if (!row) return 0;
    const pf = parseYMD(periodFrom);
    const pt = parseYMD(periodTo);
    if (!pf || !pt) return 0;

    // 1.3 none of the period exists in from–to
    if (!rentalLeaseOverlapsPeriodWindow(row, periodFrom, periodTo)) {
        return 0;
    }

    const periodMonths = monthsBetweenInclusive(pf, pt);
    if (periodMonths.length === 0) return 0;

    // 1.1 all months of the period exist under from–to → Monthly Rent × months
    if (rentalLeaseFullyCoversPeriod(row, periodFrom, periodTo)) {
        return periodMonths.reduce((sum, monthStart) => (
            sum + monthlyRentOnMonth(row, monthStart)
        ), 0);
    }

    // 1.2 partial — full months × rent + last-month day fraction (computeRentProrated)
    return computeRawLeaseRentInPeriod(row, pf, pt);
}

/** @deprecated Prefer {@link computeMultiLastTabOverAYear}; kept for call-site compat. */
export function computeRentalRemainingAfterPeriod(
    row: Pick<RentalRow, 'fromDate' | 'toDate' | 'monthlyRent' | 'renewal' | 'cancellationDate'> | null | undefined,
    periodTo: string | null | undefined,
): number {
    if (!row) return 0;
    const pt = parseYMD(periodTo);
    if (!pt) return 0;
    const overStart = new Date(pt.getFullYear(), pt.getMonth(), pt.getDate() + 1);

    const segmentRemaining = (
        fromDate: string | null | undefined,
        toDate: string | null | undefined,
        monthlyRent: number | string | null | undefined,
    ): number => {
        const rf = parseYMD(fromDate);
        let effectiveTo = toDate;
        if (String(row.cancellationDate || '').trim()) {
            effectiveTo = resolveEffectiveLeaseToDate(toDate, row.cancellationDate) ?? toDate;
        }
        const rt = parseYMD(effectiveTo);
        if (!rt || rt < overStart) return 0;
        const start = rf && rf > overStart ? rf : overStart;
        if (start > rt) return 0;
        return computeRentProrated(start, rt, toNumber(monthlyRent));
    };

    let sum = segmentRemaining(row.fromDate, row.toDate, row.monthlyRent);
    if (hasActiveRenewal(row) && row.renewal) {
        sum += segmentRemaining(row.renewal.fromDate, row.renewal.toDate, row.renewal.monthlyRent);
    }
    return sum;
}

export type RentalMultiPeriodSlice = {
    key?: string;
    periodFrom?: string | null;
    periodTo?: string | null;
};

/**
 * MULTI Rental commitment for one lease row on the active Tab.
 * - one year = this Tab All Rental Details Total
 * - over a year = next Tab All Rental Details Total (0 → caller should not roll to next Tab)
 * - last Tab over a year = {@link computeMultiLastTabOverAYear}
 */
export function computeMultiRentalCommitmentForRow(
    row: RentalRow,
    periods: RentalMultiPeriodSlice[] | null | undefined,
    activeIndex: number,
    opts?: {
        /** Current-tab All Rental Details total (when already computed). */
        currentPeriodTotal?: number;
        /** Next-tab row (rolled) for month adjustments when present. */
        nextTabRow?: RentalRow | null;
    },
): {one: number; over: number} {
    if (!Array.isArray(periods) || activeIndex < 0 || activeIndex >= periods.length) {
        return {one: 0, over: 0};
    }
    const active = periods[activeIndex];
    const one = opts?.currentPeriodTotal != null
        ? Number(opts.currentPeriodTotal) || 0
        : computeRentalRowAllDetailsTotal(row, active.periodFrom, active.periodTo);

    if (activeIndex >= periods.length - 1) {
        return {
            one,
            over: computeMultiLastTabOverAYear(row, active.periodFrom, active.periodTo),
        };
    }

    const next = periods[activeIndex + 1];
    const nextRow = opts?.nextTabRow ?? row;
    const over = computeRentalRowAllDetailsTotal(
        nextRow,
        next.periodFrom,
        next.periodTo,
    );
    return {one, over};
}

/**
 * Shift a rental period date back one calendar year (DD/MM/YYYY).
 * @param value period from or to
 * @return shifted date, or undefined when unparseable
 */
function shiftRentalDateBackOneYear(value: string | null | undefined): string | undefined {
    const parsed = parseYMD(value);
    if (!parsed) return undefined;
    return formatRentalDDMMYYYY(addYears(parsed, -1));
}

/**
 * Last-YOA rental commitment (One year / Over a year).
 * Same formula as the YOA column, with period from~to moved back one year.
 * @param rows current lease rows
 * @param args active period and MULTI tab context
 * @return precomputed one-year and over-a-year amounts
 */
export function computeRentalCommitmentShiftedBackOneYear(
    rows: RentalRow[] | null | undefined,
    args: {
        /** MULTI expense commitment (periods.length >= 2); otherwise classic next-year window. */
        multi: boolean;
        periodFrom?: string | null;
        periodTo?: string | null;
        periods?: RentalMultiPeriodSlice[] | null;
        activeIndex: number;
        nextTabRow?: (row: RentalRow) => RentalRow | null;
    },
): {one: number; over: number} {
    const list = rows || [];
    // 1. SINGLE / classic: YOA uses next-year window from periodTo only.
    if (!args.multi || args.activeIndex < 0 || !args.periods || args.periods.length < 2) {
        const window = buildNextYearWindow(shiftRentalDateBackOneYear(args.periodTo));
        return list.reduce((acc, row) => {
            acc.one += hasActiveRenewal(row)
                ? computeMergedOneYearRentFromWindow(window, row)
                : computeOneYearRentFromWindow(window, row.fromDate, row.toDate, row.monthlyRent);
            acc.over += hasActiveRenewal(row)
                ? computeMergedOverYearRentFromWindow(window, row)
                : computeOverYearRentFromWindow(window, row.fromDate, row.toDate, row.monthlyRent);
            return acc;
        }, {one: 0, over: 0});
    }
    // 2. MULTI: same tab formula, each slice from~to shifted back one year.
    const shiftedPeriods = args.periods.map((period) => ({
        ...period,
        periodFrom: shiftRentalDateBackOneYear(period.periodFrom) ?? period.periodFrom,
        periodTo: shiftRentalDateBackOneYear(period.periodTo) ?? period.periodTo,
    }));
    return list.reduce((acc, row) => {
        const part = computeMultiRentalCommitmentForRow(row, shiftedPeriods, args.activeIndex, {
            nextTabRow: args.nextTabRow?.(row) ?? null,
        });
        acc.one += part.one;
        acc.over += part.over;
        return acc;
    }, {one: 0, over: 0});
}

/**
 * Resolve Rental Deposit (REN local only).
 * Split: rentalDepositChange != null/undefined → override (incl. 0).
 * Non-split: absent → monthlyRent * 2.
 */
export function resolveRentalDeposit(row: Pick<RentalRow, 'monthlyRent' | 'rentalDepositChange' | 'otherInfo'> | null | undefined): number {
    if (isRentalDepositSplitActive(row)) {
        return Number(row?.rentalDepositChange) || 0;
    }
    return toNumber(row?.monthlyRent) * 2;
}

/**
 * REN EditRental split active (local helper — does not change MoneyField).
 * Flag on otherInfo, or rentalDepositChange present (0 is valid override).
 */
export function isRentalDepositSplitActive(
    row: Pick<RentalRow, 'rentalDepositChange' | 'otherInfo'> | null | undefined,
): boolean {
    const flagged = (row?.otherInfo as {rentalDepositSplitActive?: boolean} | undefined)?.rentalDepositSplitActive;
    if (flagged === true) return true;
    if (flagged === false) return false;
    return row?.rentalDepositChange != null;
}

/**
 * REN local: undefined = non-split; finite number incl. 0 = split override.
 * Maps API/DB null → undefined so shared RentalRow type stays `number | undefined`.
 */
export function normalizeRentalDepositChange(change: number | null | undefined): number | undefined {
    if (change == null) return undefined;
    const n = Number(change);
    return Number.isFinite(n) ? n : undefined;
}

export function computeRentalDepositTotal(rows: RentalRow[] | null | undefined): number {
    return (rows || []).reduce((sum, row) => sum + resolveRentalDeposit(row), 0);
}

export function hasRentalExpenseData(rows: RentalRow[] | null | undefined): boolean {
    return (rows || []).some((row) => {
        const periods = normalizeRentFreePeriods(row?.rentFreePeriods);
        return Boolean(
            String(row?.address || '').trim() ||
            String(row?.agreementDate || '').trim() ||
            String(row?.landlord || '').trim() ||
            String(row?.tenant || '').trim() ||
            String(row?.fromDate || '').trim() ||
            String(row?.toDate || '').trim() ||
            String(row?.monthlyRent || '').trim() ||
            hasActiveRenewal(row) ||
            periods.length > 0
        );
    });
}

/**
 * UI empty draft after deleteIcon — must not be persisted (backend soft-deletes omitted ids).
 * Matches RentalExpenseWp validateRentalRows blank skip.
 */
export function isBlankRentalRow(row: RentalRow | null | undefined): boolean {
    if (!row) return true;
    const addressEmpty = !String(row.address || '').trim();
    const rent = String(row.monthlyRent || '').trim();
    const monthlyRentEmpty = !rent || rent === '0';
    const hasOtherData = !!(
        String(row.landlord || '').trim()
        || String(row.tenant || '').trim()
        || String(row.fromDate || '').trim()
        || String(row.toDate || '').trim()
        || hasActiveRenewal(row)
    );
    return addressEmpty && monthlyRentEmpty && !hasOtherData;
}

export type RentalSharedOtherMeta = {
    monthsForPeriod?: string | null;
    /** null = follow calculated; 0 = intentional Split override. */
    lastYearOneAYear?: number | null;
    lastYearOverAYear?: number | null;
    lastYearTotalRental?: number;
};

/** Preserve null vs 0 (avoid `||` swallowing intentional 0). */
function readRentalMoneyOrNull(v: unknown): number | null {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

export type RentalYoaPeriodSlice = {
    key?: string;
    yearOfAssessment?: string;
    periodFrom?: string;
    periodTo?: string;
};

/** Months-count label for a period window (MULTI reverse-calc otherInfo.monthsForPeriod). */
export function resolveMonthsForPeriodLabel(
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
    fallback?: string | null,
): string {
    const pf = parseYMD(periodFrom);
    const pt = parseYMD(periodTo);
    if (pf && pt) {
        const n = monthsBetweenInclusive(pf, pt).length;
        if (n > 0) return String(n);
    }
    const fb = String(fallback || '').trim();
    return fb || '12';
}

/**
 * Reverse-calc row otherInfo for one YOA period.
 * - rentalTotalsByCol keyed only by this row's clientKey (never prior-origin keys)
 * - monthsForPeriod from the period length
 * - keeps period-local deletedMonths / monthChanges / extraExpense for this clientKey
 */
export function recomputeRentalRowOtherInfoForPeriod(
    row: RentalRow | null | undefined,
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
    shared?: RentalSharedOtherMeta | null,
): NonNullable<RentalRow['otherInfo']> {
    const rowKey = String(row?.clientKey || '');
    const prev = (row?.otherInfo || {}) as {
        deletedMonthsByCol?: Record<string, string[]>;
        monthChangesByCol?: Record<string, Record<string, number>>;
        extraExpenseRows?: unknown[];
        lastYearOneAYear?: number | null;
        lastYearOverAYear?: number | null;
        id?: number;
        rentalId?: number;
        lineId?: number;
        extractPL?: string;
    };

    const deletedMonths = rowKey ? (prev.deletedMonthsByCol?.[rowKey] || []) : [];
    const monthChanges = rowKey ? (prev.monthChangesByCol?.[rowKey] || {}) : {};
    const monthsForPeriod = resolveMonthsForPeriodLabel(
        periodFrom,
        periodTo,
        shared?.monthsForPeriod,
    );

    const overlaps = rentalLeaseOverlapsPeriodWindow(row, periodFrom, periodTo);
    let rentalTotal: number | undefined;
    if (rowKey && overlaps && periodFrom && periodTo) {
        rentalTotal = computeRentalRowAllDetailsTotal(row, periodFrom, periodTo, {
            deletedMonths,
            monthChanges,
        });
    }

    const next: Record<string, unknown> = {
        rentalDepositSplitActive: isRentalDepositSplitActive(row),
        monthsForPeriod,
        lastYearOneAYear: shared?.lastYearOneAYear !== undefined
            ? shared.lastYearOneAYear
            : readRentalMoneyOrNull(prev.lastYearOneAYear),
        lastYearOverAYear: shared?.lastYearOverAYear !== undefined
            ? shared.lastYearOverAYear
            : readRentalMoneyOrNull(prev.lastYearOverAYear),
    };
    if (prev.id != null) next.id = prev.id;
    if (prev.rentalId != null) next.rentalId = prev.rentalId;
    if (prev.lineId != null) next.lineId = prev.lineId;
    if (prev.extractPL != null) next.extractPL = prev.extractPL;
    if (rowKey && deletedMonths.length > 0) {
        next.deletedMonthsByCol = {[rowKey]: deletedMonths};
    }
    if (rowKey && Object.keys(monthChanges).length > 0) {
        next.monthChangesByCol = {[rowKey]: monthChanges};
    }
    // Keep empty [] so Save clears DB extras + backend auto-orphan (null = skip).
    if (Array.isArray(prev.extraExpenseRows)) {
        next.extraExpenseRows = prev.extraExpenseRows;
    }
    if (rowKey && rentalTotal != null) {
        next.rentalTotalsByCol = {[rowKey]: rentalTotal};
    }
    return next as NonNullable<RentalRow['otherInfo']>;
}

/**
 * Assemble one YOA bucket details for REN MULTI batchSave:
 * `{ rows, rentalOtherInfo?, … }` — no WP meta / roll markers.
 */
export function assembleRentalDetailsForYoaSave(args: {
    rows: RentalRow[] | null | undefined;
    periodFrom?: string | null;
    periodTo?: string | null;
    annualId: number;
    headerId?: string | null;
    shared?: RentalSharedOtherMeta | null;
    relatedItems?: unknown;
    /** Rental income toolbar rows */
    transactionTest?: unknown;
    cutOffThisYearTest?: unknown;
    cutOffNextYearTest?: unknown;
    removeAdjustHeaderIds?: Array<number | string>;
    includeLastYearTotalRental?: boolean;
}): Record<string, unknown> {
    const {
        periodFrom,
        periodTo,
        annualId,
        headerId,
        shared,
        includeLastYearTotalRental,
    } = args;
    const monthsForPeriod = resolveMonthsForPeriodLabel(
        periodFrom,
        periodTo,
        shared?.monthsForPeriod,
    );
    const sharedMeta: RentalSharedOtherMeta = {
        monthsForPeriod,
        lastYearOneAYear: shared?.lastYearOneAYear !== undefined
            ? shared.lastYearOneAYear
            : null,
        lastYearOverAYear: shared?.lastYearOverAYear !== undefined
            ? shared.lastYearOverAYear
            : null,
        lastYearTotalRental: shared?.lastYearTotalRental,
    };

    const rowsWithOther = (args.rows || []).map((row) => ({
        ...row,
        otherInfo: recomputeRentalRowOtherInfoForPeriod(row, periodFrom, periodTo, sharedMeta),
    }));

    const rentalRows = normalizeRentalRowsForSave(
        splitRenewalRowsForSave(rowsWithOther),
        annualId,
        headerId,
    ).map((row) => ({
        ...row,
        // Re-apply period-scoped otherInfo after normalize (normalize preserves maps)
        otherInfo: recomputeRentalRowOtherInfoForPeriod(row, periodFrom, periodTo, sharedMeta),
    }));

    const details: Record<string, unknown> = {
        rows: rentalRows,
        rentalOtherInfo: {
            monthsForPeriod,
            lastYearOneAYear: sharedMeta.lastYearOneAYear,
            lastYearOverAYear: sharedMeta.lastYearOverAYear,
            ...(includeLastYearTotalRental
                ? {lastYearTotalRental: Number(sharedMeta.lastYearTotalRental) || 0}
                : {}),
        },
    };
    if (args.relatedItems != null) details.relatedItems = args.relatedItems;
    if (args.transactionTest != null) details.transactionTest = args.transactionTest;
    if (args.cutOffThisYearTest != null) details.cutOffThisYearTest = args.cutOffThisYearTest;
    if (args.cutOffNextYearTest != null) details.cutOffNextYearTest = args.cutOffNextYearTest;
    if (args.removeAdjustHeaderIds && args.removeAdjustHeaderIds.length > 0) {
        details.removeAdjustHeaderIds = [...args.removeAdjustHeaderIds];
    }
    return details;
}

/**
 * Build full YOA Map for REN MULTI batchSave:
 * `{ [yoa]: { details }, issueMark? }` — each year's otherInfo reverse-calc'd for that period.
 */
export function buildRentalMultiYearBatchPayload(args: {
    periods: RentalYoaPeriodSlice[];
    lineCode: string;
    annualId: number;
    headerId?: string | null;
    activeYoa?: string | null;
    activeDetails?: Record<string, unknown> | null;
    getDetail: (draftKey: string) => unknown;
    extractRows: (detail: unknown) => RentalRow[];
    extractShared?: (detail: unknown) => RentalSharedOtherMeta | null | undefined;
    relatedItems?: unknown;
    transactionTest?: unknown;
    cutOffThisYearTest?: unknown;
    cutOffNextYearTest?: unknown;
    removeAdjustHeaderIds?: Array<number | string>;
    includeLastYearTotalRental?: boolean;
    issueMark?: {marked: boolean; remark?: string} | null;
}): Record<string, unknown> | null {
    const periods = (args.periods || []).filter((p) => String(p?.yearOfAssessment || '').trim());
    if (periods.length < 2) return null;

    const activeYoa = String(args.activeYoa || '').trim();
    const payload: Record<string, unknown> = {};

    for (const period of periods) {
        const yoa = String(period.yearOfAssessment || '').trim();
        if (!yoa) continue;

        if (activeYoa && yoa === activeYoa && args.activeDetails) {
            payload[yoa] = {details: args.activeDetails};
            continue;
        }

        const sliceKey = String(period.key || '').trim();
        const draft = sliceKey
            ? args.getDetail(`${args.lineCode}__${sliceKey}`)
            : null;
        const rows = args.extractRows(draft);
        const shared = args.extractShared?.(draft) || undefined;
        const assembled = assembleRentalDetailsForYoaSave({
            rows,
            periodFrom: period.periodFrom,
            periodTo: period.periodTo,
            annualId: args.annualId,
            headerId: args.headerId,
            shared,
            // Toolbar / related only on active slice — other YOAs keep row data only
            includeLastYearTotalRental: args.includeLastYearTotalRental,
        });
        payload[yoa] = {details: assembled};
    }

    if (args.issueMark) {
        payload.issueMark = args.issueMark;
    }

    const yoaKeys = periods.map((p) => String(p.yearOfAssessment || '').trim()).filter(Boolean);
    const missing = yoaKeys.filter((y) => !payload[y]);
    if (missing.length > 0) return null;
    return payload;
}

export function normalizeRentalRowsForSave(rows: RentalRow[] | null | undefined, annualId: number, headerId?: string | null): RentalRow[] {
    return (rows || []).filter((row) => !isBlankRentalRow(row)).map((row) => {
        const rowKey = String(row?.clientKey || '');
        const prev = (row?.otherInfo || {}) as {
            deletedMonthsByCol?: Record<string, string[]>;
            monthChangesByCol?: Record<string, Record<string, number>>;
            rentalTotalsByCol?: Record<string, number>;
            extraExpenseRows?: unknown[];
            monthsForPeriod?: string | null;
            lastYearOneAYear?: number | null;
            lastYearOverAYear?: number | null;
            id?: number;
            rentalId?: number;
            lineId?: number;
            extractPL?: string;
        };
        // Keep only this clientKey's col maps — never leak prior-origin keys on rolled rows
        const otherInfo: Record<string, unknown> = {
            rentalDepositSplitActive: isRentalDepositSplitActive(row),
        };
        if (prev.id != null) otherInfo.id = prev.id;
        if (prev.rentalId != null) otherInfo.rentalId = prev.rentalId;
        if (prev.lineId != null) otherInfo.lineId = prev.lineId;
        if (prev.extractPL != null) otherInfo.extractPL = prev.extractPL;
        if (prev.monthsForPeriod != null) otherInfo.monthsForPeriod = prev.monthsForPeriod;
        // Preserve null (follow calculated) and 0 (Split override) alike.
        if ('lastYearOneAYear' in prev) otherInfo.lastYearOneAYear = prev.lastYearOneAYear ?? null;
        if ('lastYearOverAYear' in prev) otherInfo.lastYearOverAYear = prev.lastYearOverAYear ?? null;
        // Keep empty [] so Save clears DB extras + backend auto-orphan (null = skip).
        if (Array.isArray(prev.extraExpenseRows)) {
            otherInfo.extraExpenseRows = prev.extraExpenseRows;
        }
        if (rowKey && prev.deletedMonthsByCol?.[rowKey]?.length) {
            otherInfo.deletedMonthsByCol = {[rowKey]: prev.deletedMonthsByCol[rowKey]};
        }
        if (rowKey && prev.monthChangesByCol?.[rowKey]
            && Object.keys(prev.monthChangesByCol[rowKey]).length > 0) {
            otherInfo.monthChangesByCol = {[rowKey]: prev.monthChangesByCol[rowKey]};
        }
        if (rowKey && prev.rentalTotalsByCol?.[rowKey] != null) {
            otherInfo.rentalTotalsByCol = {[rowKey]: prev.rentalTotalsByCol[rowKey]};
        }

        return {
            id: row?.id,
            annualId,
            lineId: row?.lineId,
            code: row?.code || 'PL-AE-REN',
            clientKey: rowKey,
            address: String(row?.address || ''),
            agreementDate: String(row?.agreementDate || ''),
            landlord: String(row?.landlord || ''),
            tenant: String(row?.tenant || ''),
            // Transient: Director / ShareHolder person id + ID Number for account-table sync on WP save
            tenantPersonId: row?.tenantPersonId ?? null,
            tenantPersonIdNo: row?.tenantPersonIdNo ?? null,
            // null clears DB when leaving Other; omit pendingDelete* / roll markers from wire
            leaseAgreementLink: row?.leaseAgreementLink != null && String(row.leaseAgreementLink).trim() !== ''
                ? String(row.leaseAgreementLink)
                : null,
            rentalPeriod: String(row?.rentalPeriod || ''),
            fromDate: String(row?.fromDate || ''),
            toDate: String(row?.toDate || ''),
            monthlyRent: String(row?.monthlyRent || ''),
            rentFreePeriods: normalizeRentFreePeriods(row?.rentFreePeriods),
            cancellationDate: String(row?.cancellationDate || ''),
            // Wire: null = non-split（清空 DB 覆盖值）；number incl. 0 = split
            // Must send null explicitly so backend clears rental_deposit_change
            rentalDepositChange: isRentalDepositSplitActive(row)
                ? (Number(row?.rentalDepositChange) || 0)
                : null,
            otherInfo: otherInfo as RentalRow['otherInfo'],
            headerId: String(headerId || row?.headerId || ''),
            adjustments: row?.adjustments ?? null,
            // rentalIsRolled / rentalOriginKey / pendingDelete* intentionally omitted
        };
    });
}
